import { useEffect, useState } from "react";
import { useAuth } from "../../Contexts/AuthContext";
import { accountService } from "../../Api/Services/accountService";
import { SimplifiedUser, UserLogin, UserRegister } from "../../Models/User";
import { CustomerType } from "../../Enums/CustomerType";
import { toPublicPath } from "../../Utils/publicRoutes";
import { formatCnpj, formatCpf, formatPhoneNumber, unformat } from "../../Utils/DataFormatters";
import { isValidPassword, passwordRequirementsMessage } from "../../Utils/passwordValidator";
import { Input } from "../Input/Input";
import { GoogleIcon } from "../GoogleIcon/GoogleIcon";
import { FaLock, FaSignInAlt, FaTimes, FaUser, FaUserPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import ForgotPasswordFlow from "../ForgotPasswordFlow/ForgotPasswordFlow";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import PersonTypeToggle from "../PersonTypeToggle/PersonTypeToggle";

interface CartAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: () => void;
  initialTab?: 'login' | 'register';
}

const CartAuthModal = ({ isOpen, onClose, onAuthSuccess, initialTab = 'login' }: CartAuthModalProps) => {
  const { login, register, googlePopupLogin, logout, updateUser } = useAuth();

  const [tab, setTab] = useState<'login' | 'register' | 'complete-contact'>(initialTab);

  useEffect(() => {
    if (isOpen) {
      setTab(initialTab);
    }
  }, [isOpen, initialTab]);
  const [loginData, setLoginData] = useState<UserLogin>({ email: "", password: "" });
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
  const [isForgotPasswordFlowOpen, setIsForgotPasswordFlowOpen] = useState(false);

  // Register state
  const [personType, setPersonType] = useState(CustomerType.IndividualPerson);
  const [cpf, setCpf] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isRegisterSubmitting, setIsRegisterSubmitting] = useState(false);

  // Complete contact state (shown after Google sign-in when requiresContactInfoUpdate)
  const [pendingUser, setPendingUser] = useState<SimplifiedUser | null>(null);
  const [contactCustomerType, setContactCustomerType] = useState(CustomerType.IndividualPerson);
  const [contactCpf, setContactCpf] = useState("");
  const [contactCnpj, setContactCnpj] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoginSubmitting) {
      return;
    }

    setIsLoginSubmitting(true);
    const loggedInUser = await login(loginData);
    setIsLoginSubmitting(false);

    if (!loggedInUser) {
      return;
    }
    if (loggedInUser.requiresContactInfoUpdate) {
      setPendingUser(loggedInUser);
      setTab('complete-contact');
      return;
    }
    onAuthSuccess();
  };

  const handleGoogleSignIn = () => {
    setIsGoogleLoading(true);
    void googlePopupLogin('signin').then((u) => {
      setIsGoogleLoading(false);
      if (!u) {
        return;
      }
      if (u.requiresContactInfoUpdate) {
        setPendingUser(u);
        setTab('complete-contact');
        return;
      }
      onAuthSuccess();
    });
  };

  const handleGoogleSignUp = () => {
    setIsGoogleLoading(true);
    void googlePopupLogin('signup').then((u) => {
      setIsGoogleLoading(false);
      if (!u) {
        return;
      }
      if (u.requiresContactInfoUpdate) {
        setPendingUser(u);
        setTab('complete-contact');
        return;
      }
      onAuthSuccess();
    });
  };

  const handleCompleteContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isContactSubmitting) {
      return;
    }

    if (pendingUser?.requiresEmailUpdate && !contactEmail) {
      toast.error("Preencha o e-mail.");
      return;
    }
    if (pendingUser?.requiresPhoneUpdate && !contactPhone) {
      toast.error("Preencha o telefone.");
      return;
    }
    const documentRequired = !pendingUser?.requiresPhoneUpdate && !pendingUser?.requiresEmailUpdate;
    if (documentRequired || contactCpf.length > 0 || contactCnpj.length > 0) {
      if (contactCustomerType === CustomerType.IndividualPerson && contactCpf.length < 11) {
        toast.error("Preencha o CPF.");
        return;
      }
      if (contactCustomerType === CustomerType.LegalEntity && contactCnpj.length < 14) {
        toast.error("Preencha o CNPJ.");
        return;
      }
    }

    const sendDocument = documentRequired || contactCpf.length >= 11 || contactCnpj.length >= 14;
    const payload: Parameters<typeof accountService.completeContactInfo>[0] = {
      email: pendingUser?.requiresEmailUpdate ? contactEmail : undefined,
      phoneNumber: pendingUser?.requiresPhoneUpdate ? contactPhone : undefined,
      customerType: sendDocument ? contactCustomerType : undefined,
      cpf: sendDocument && contactCustomerType === CustomerType.IndividualPerson ? contactCpf : undefined,
      cnpj: sendDocument && contactCustomerType === CustomerType.LegalEntity ? contactCnpj : undefined,
    };

    setIsContactSubmitting(true);
    try {
      const refreshedUser = await accountService.completeContactInfo(payload);
      updateUser(refreshedUser);
      toast.success("Cadastro completado com sucesso!");
      setPendingUser(null);
      setContactCpf(""); setContactCnpj(""); setContactPhone(""); setContactEmail("");
      setTab('login');
      onAuthSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data || "Não foi possível salvar seus dados.";
      toast.error(msg);
    } finally {
      setIsContactSubmitting(false);
    }
  };

  const handlePersonTypeChange = (type: CustomerType) => {
    setPersonType(type);
    setCpf("");
    setCnpj("");
    setPhoneNumber("");
    setEmail("");
    setPassword("");
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isRegisterSubmitting) {
      return;
    }

    if (!email || !phoneNumber) {
      toast.error("Preencha email e telefone.");
      return;
    }

    if (personType === CustomerType.IndividualPerson) {
      if (!cpf || cpf.length < 11) {
        toast.error("Preencha o CPF.");
        return;
      }
    } else {
      if (!cnpj || cnpj.length < 14) {
        toast.error("Preencha o CNPJ.");
        return;
      }
    }

    if (!isValidPassword(password)) {
      toast.error(passwordRequirementsMessage);
      return;
    }

    let payload: UserRegister;
    if (personType === CustomerType.IndividualPerson) {
      payload = {
        email,
        phoneNumber,
        password,
        customerType: CustomerType.IndividualPerson,
        individualPerson: {
          cpf
        },
        legalEntity: null
      };
    } else {
      payload = {
        email,
        phoneNumber,
        password,
        customerType: CustomerType.LegalEntity,
        individualPerson: null,
        legalEntity: {
          cnpj
        }
      };
    }

    setIsRegisterSubmitting(true);
    try {
      const success = await register(payload);
      if (success) {
        const loggedInUser = await login({ email, password });
        if (loggedInUser?.requiresContactInfoUpdate) {
          setPendingUser(loggedInUser);
          setTab('complete-contact');
        } else if (loggedInUser) {
          onAuthSuccess();
        } else {
          setTab('login');
        }
      }
    } finally {
      setIsRegisterSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    setIsGoogleLoading(false);
    if (tab === 'complete-contact') {
      await logout({ silent: true });
      setPendingUser(null);
      setTab('login');
    }
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Modal overlayClassName="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-4" closeOnBackdropClick onClose={() => void handleDismiss()}>
        <div className={`bg-white rounded-lg shadow-lg w-full p-6 relative max-h-[90vh] supports-[height:100dvh]:max-h-[90dvh] overflow-y-auto transition-all duration-300 ease-in-out ${tab === 'register' ? 'max-w-2xl' : 'max-w-sm'}`}>
          <button onClick={() => void handleDismiss()} className="absolute top-4 right-4 text-xl text-primary-600 hover:text-primary-700" aria-label="Fechar">
            <FaTimes />
          </button>

          {isGoogleLoading && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-primary-600 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Aguardando autenticação...</p>
            </div>
          )}

          {!isGoogleLoading && tab !== 'complete-contact' && (
            <div className="flex mb-6 border-b">
              <button type="button" onClick={() => setTab('login')} className={`flex-1 pb-2 text-sm font-semibold border-b-2 transition-colors ${tab === 'login' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Entrar</button>
              <button type="button" onClick={() => setTab('register')} className={`flex-1 pb-2 text-sm font-semibold border-b-2 transition-colors ${tab === 'register' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Criar conta</button>
            </div>
          )}

          {!isGoogleLoading && tab === 'login' && (
            <div>
              <div className="mb-4 space-y-3">
                <button type="button" className="w-full border border-gray-300 bg-white text-gray-800 font-semibold py-3 rounded flex justify-center items-center gap-2 transition-colors hover:border-primary-600" onClick={handleGoogleSignIn}>
                  <GoogleIcon size={20} />
                  ENTRAR COM GOOGLE
                </button>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="h-px flex-1 bg-gray-300" /><span>ou</span><span className="h-px flex-1 bg-gray-300" />
                </div>
              </div>
              <form className="space-y-3" onSubmit={handleLoginSubmit}>
                <Input label="E-mail" name="email" type="email" placeholder="seu@email.com" value={loginData.email} onChange={handleLoginChange} required leftIcon={<FaUser />} />
                <Input label="Senha" name="password" type="password" placeholder="Sua senha" value={loginData.password} onChange={handleLoginChange} required leftIcon={<FaLock />} />
                <Button isLoading={isLoginSubmitting} icon={<FaSignInAlt />} type="submit" className="w-full py-3 flex justify-center items-center gap-2" disabled={isLoginSubmitting}>
                  {"ENTRAR"}
                </Button>
                <div className="text-right">
                  <button type="button" onClick={() => setIsForgotPasswordFlowOpen(true)} className="text-sm text-gray-500 underline hover:text-primary-600">Esqueceu a senha?</button>
                </div>
              </form>
              <div className="w-full flex justify-center items-center text-sm gap-1 pt-4">
                <p>Não tem conta?</p>
                <button type="button" onClick={() => setTab('register')} className="text-primary-600 underline font-bold hover:text-primary-700">CADASTRAR-SE</button>
              </div>
            </div>
          )}

          {!isGoogleLoading && tab === 'register' && (
            <div>
              <div className="mb-4 space-y-3">
                <button type="button" className="w-full border border-gray-300 bg-white text-gray-800 font-semibold py-3 rounded flex justify-center items-center gap-2 transition-colors hover:border-primary-600" onClick={handleGoogleSignUp}>
                  <GoogleIcon size={20} />
                  CONTINUAR COM GOOGLE
                </button>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="h-px flex-1 bg-gray-300" /><span>ou</span><span className="h-px flex-1 bg-gray-300" />
                </div>
              </div>

              <PersonTypeToggle value={personType} onChange={handlePersonTypeChange} className="gap-3 mb-4" />

              <form onSubmit={handleRegisterSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {personType === CustomerType.IndividualPerson ? (
                    <Input
                      label="CPF"
                      type="text"
                      name="cpf"
                      placeholder="000.000.000-00"
                      value={formatCpf(cpf)}
                      onChange={(e) => setCpf(unformat(e.target.value).slice(0, 11))}
                      required
                    />
                  ) : (
                    <Input
                      label="CNPJ"
                      type="text"
                      name="cnpj"
                      placeholder="00.000.000/0000-00"
                      value={formatCnpj(cnpj)}
                      onChange={(e) => setCnpj(unformat(e.target.value).slice(0, 14))}
                      required
                    />
                  )}
                  <Input label="Telefone celular" type="tel" name="phoneNumber" placeholder="(00) 00000-0000" value={formatPhoneNumber(phoneNumber)} onChange={(e) => setPhoneNumber(unformat(e.target.value))} required />
                  <Input label="E-mail" type="email" name="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  <Input label="Senha" type="password" name="password" placeholder="Crie sua senha" tooltip={passwordRequirementsMessage.trim()} value={password} onChange={(e) => setPassword(e.target.value)} required />
                  <p className="sm:col-span-2 text-xs text-gray-500 my-1">Ao criar uma conta, você concorda com as{" "}<Link to={toPublicPath("/politicas")} className="text-primary-600 underline" target="_blank">Políticas do Site e Políticas de Privacidade</Link>.</p>
                  <Button isLoading={isRegisterSubmitting} icon={<FaUserPlus />} type="submit" className="sm:col-span-2 w-full py-3 flex justify-center items-center gap-2" disabled={isRegisterSubmitting}>{"CRIAR CONTA"}</Button>
                </div>
                <div className="w-full flex justify-center items-center text-sm gap-1 pt-4">
                  <p>Já tem conta?</p>
                  <button type="button" onClick={() => setTab('login')} className="text-primary-600 underline font-bold hover:text-primary-700">ENTRAR</button>
                </div>
              </form>
            </div>
          )}

          {!isGoogleLoading && tab === 'complete-contact' && (
            <div>
              <h3 className="text-center font-semibold text-primary-600 text-base mb-1">Completar dados de contato</h3>
              <p className="text-xs text-gray-500 text-center mb-4">
                {pendingUser?.requiresEmailUpdate && pendingUser?.requiresPhoneUpdate ? "Precisamos do seu e-mail e telefone para liberar seu acesso." :
                 pendingUser?.requiresEmailUpdate ? "Precisamos do seu e-mail para liberar seu acesso." :
                 pendingUser?.requiresPhoneUpdate ? "Precisamos do seu telefone para liberar seu acesso." :
                 "Complete seus dados para continuar."}
              </p>
              <form onSubmit={handleCompleteContactSubmit} className="space-y-3">
                {pendingUser?.requiresEmailUpdate && (
                  <Input label="E-mail" type="email" name="contactEmail" placeholder="seu@email.com" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required />
                )}
                {pendingUser?.requiresPhoneUpdate && (
                  <Input label="Telefone celular" type="tel" name="contactPhone" placeholder="(00) 00000-0000" value={formatPhoneNumber(contactPhone)} onChange={(e) => setContactPhone(unformat(e.target.value))} required />
                )}
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-500 mb-2">Informe seu CPF ou CNPJ:</p>
                  <PersonTypeToggle
                    value={contactCustomerType}
                    onChange={(type) => { setContactCustomerType(type); setContactCpf(""); setContactCnpj(""); }}
                    className="gap-3 mb-3"
                  />
                  {contactCustomerType === CustomerType.IndividualPerson ? (
                    <Input label="CPF" type="text" name="contactCpf" placeholder="000.000.000-00" value={formatCpf(contactCpf)} onChange={(e) => setContactCpf(unformat(e.target.value).slice(0, 11))} required />
                  ) : (
                    <Input label="CNPJ" type="text" name="contactCnpj" placeholder="00.000.000/0000-00" value={formatCnpj(contactCnpj)} onChange={(e) => setContactCnpj(unformat(e.target.value).slice(0, 14))} required />
                  )}
                </div>
                <Button isLoading={isContactSubmitting} type="submit" disabled={isContactSubmitting} className="w-full py-3">
                  {"SALVAR E CONTINUAR"}
                </Button>
              </form>
            </div>
          )}
        </div>
      </Modal>

      <ForgotPasswordFlow isOpen={isForgotPasswordFlowOpen} onClose={() => setIsForgotPasswordFlowOpen(false)} />
    </>
  );
};

export default CartAuthModal;
